/**
 * MED-921 - the mapping between the patient index filter form and the API's query parameters,
 * plus the two readers the patient screens need for fields the API may not send at all.
 *
 * Kept out of the components because both halves are logic rather than markup, and the jsdom
 * test suites in this repo cannot run - so anything worth testing has to be testable without a
 * DOM. See patientFilters.test.js.
 */

export const NAME_FILTER = 'name';

/**
 * A date of birth is a disambiguator here, not a range to browse: it is what tells apart the
 * 1,091 names in this estate that are shared by more than one person. So the form has one date
 * input meaning "born on this day".
 *
 * API Platform's DateFilter offers no exact strategy, so that one date becomes a closed range of
 * a single day. `dateOfBirth` is a DATE column, so after-and-before on the same date is exactly
 * that day and nothing either side of it.
 *
 * The `(after)` spelling rather than `[after]` is this codebase's convention: redux-form treats
 * brackets in a field name as a path into nested state, so parseFilterObjectToQueryString swaps
 * the parentheses back to brackets on the way out.
 */
export const DOB_FROM_FILTER = 'dateOfBirth(after)';
export const DOB_TO_FILTER = 'dateOfBirth(before)';

/**
 * Narrowing the register to one consultant's patients.
 *
 * `Patient` carries no consultant of its own - identity is global, because 2,242 people are seen
 * by more than one - so the filter reaches through the patient's invoices. This is the API
 * property path, not a column.
 *
 * It never narrows the invoice count or the consultant list on the rows that come back: those
 * stay the whole person's. A row reading "1 invoice" under a consultant filter would be
 * describing the filter rather than the human.
 */
export const CONSULTANT_FILTER = 'invoices.consultant';

/**
 * Filters are serialised by parseFilterObjectToQueryString with `encode: false`, so whatever is
 * typed lands in the query string verbatim - a stray `&` or `=` would split the parameter and
 * quietly run a different search than the one on screen.
 *
 * Letters, marks, spaces, apostrophes and hyphens are kept, and nothing else. Accents and
 * hyphens have to survive: the production copy holds 372 apostrophed and 3,207 hyphenated
 * surnames, and dropping either would make those people unsearchable. Whitespace is collapsed
 * because the API splits the term on it and an empty word would match everything.
 */
const searchTerm = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/[^\p{L}\p{M}\s'-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * A date is only accepted in the exact shape the date input produces. Anything else is treated as
 * no date at all rather than passed along - see the note on searchTerm.
 */
const isoDate = (value) => (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? value.trim() : null);

const asFieldValue = (value) => (value === null || value === undefined ? '' : value);

/**
 * Rewrites the form values into API parameters, leaving every other filter alone.
 *
 * A cleared filter is set to null rather than removed, because that is how the containers switch
 * one off: they drop null keys when merging default, active and session filters. Removing the key
 * instead would let a stale session filter survive a reset.
 */
export const applyPatientFilters = (values, selectedConsultants = []) => {
  const filters = { ...values };

  const name = searchTerm(filters[NAME_FILTER]);
  filters[NAME_FILTER] = name === '' ? null : name;

  const dob = isoDate(filters.dob);
  filters[DOB_FROM_FILTER] = dob;
  filters[DOB_TO_FILTER] = dob;
  delete filters.dob;

  // The typeahead holds whole consultant objects; the API wants their IRIs. Anything without one
  // is dropped rather than serialised as undefined, which would reach the query string verbatim.
  const iris = (Array.isArray(selectedConsultants) ? selectedConsultants : [])
    .map((consultant) => consultant && consultant['@id'])
    .filter((iri) => typeof iri === 'string' && iri !== '');

  filters[CONSULTANT_FILTER] = iris.length > 0 ? iris : null;

  // The redux-form field the typeahead writes to is not a filter - the selection is read from
  // component state instead, the same way InvoiceTableFilterForm does it.
  delete filters.consultant;

  return filters;
};

/**
 * Reads a stored search back into the form fields so it survives a refresh.
 */
export const patientInitialValues = (session) => {
  const stored = session || {};

  return {
    name: asFieldValue(stored[NAME_FILTER]),
    dob: asFieldValue(stored[DOB_FROM_FILTER]),
  };
};

/**
 * A patient's name as it should appear on screen.
 *
 * Every part of this is optional in the response, and not because of an edge case: API Platform
 * omits null fields entirely, `salutation` is populated for nobody yet, and two people in the
 * estate have no first name. Reading the fields straight would print "undefined Trelawny".
 */
export const patientDisplayName = (patient) => {
  if (!patient) {
    return '';
  }

  return [patient.salutation, patient.firstName, patient.lastName].filter((part) => part !== null && part !== undefined && part !== '').join(' ');
};

/**
 * The consultants a patient has been seen by, as the API stamped them.
 *
 * Empty for a patient with no invoices, which the register allows even though the backfill does
 * not produce one.
 */
export const patientConsultantNames = (patient) => {
  if (!patient || !Array.isArray(patient.consultants)) {
    return [];
  }

  return patient.consultants.map((consultant) => consultant.name);
};

/**
 * The patient's id out of the IRI the invoice response carries.
 *
 * `invoice.patient` serializes as `/patients/123`, and is absent entirely when the invoice has no
 * patient linked - which is a normal state, not an error: `invoice.patient_id` is nullable and
 * stays nullable, so an invoice that arrives before the register resolves it is still a valid
 * invoice with nothing to link to.
 *
 * Returns null rather than throwing for anything that is not an id, so the caller's decision is
 * simply "link, or do not link".
 */
export const patientIdFromIri = (iri) => {
  if (typeof iri !== 'string') {
    return null;
  }

  const matched = iri.match(/^\/patients\/(\d+)$/);

  return matched ? matched[1] : null;
};
